import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { Helmet } from '@/components/Helmet';
import { toast } from 'sonner';

type PurchaseRequestStatus = 'pending' | 'checkout_started' | 'completed' | 'expired' | 'revoked' | 'superseded';

type PurchaseStatusResponse = {
  success: boolean;
  request: {
    id: string;
    status: PurchaseRequestStatus;
    expiresAt: string;
    checkoutSessionId: string | null;
    domainCount: number;
  };
  domains: Array<{
    id: string;
    domainName: string;
    status: string;
    tier: string;
    stripePriceId: string | null;
  }>;
};

const formatStatusTitle = (status: PurchaseRequestStatus) => {
  if (status === 'pending' || status === 'checkout_started') return 'Complete Purchase';
  if (status === 'completed') return 'Purchase Complete';
  if (status === 'expired') return 'Purchase Link Expired';
  if (status === 'superseded') return 'Purchase Link Replaced';
  return 'Purchase Request Unavailable';
};

const formatStatusDescription = (status: PurchaseRequestStatus) => {
  if (status === 'pending' || status === 'checkout_started') {
    return 'Review the configured domains and continue to Stripe Checkout to activate billing and monitoring.';
  }
  if (status === 'completed') {
    return 'This purchase request has already been completed.';
  }
  if (status === 'expired') {
    return 'This link has expired. Ask your admin team to resend the purchase email.';
  }
  if (status === 'superseded') {
    return 'A newer purchase request has replaced this link. Use the latest email from your admin team.';
  }
  return 'This purchase request is no longer active.';
};

export default function CompletePurchase() {
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<PurchaseStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [startingCheckout, setStartingCheckout] = useState(false);

  const requestToken = useMemo(() => searchParams.get('request')?.trim() || '', [searchParams]);

  const loadStatus = useCallback(async () => {
    if (!requestToken) {
      setStatus(null);
      setStatusError('The purchase link is missing or invalid.');
      setStatusLoading(false);
      return;
    }

    setStatusLoading(true);
    setStatusError(null);
    try {
      const { data, error } = await supabase.functions.invoke('customer-domain-purchase', {
        body: {
          action: 'status',
          requestToken,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to load purchase request.');
      }
      setStatus(data as PurchaseStatusResponse);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load purchase request.';
      setStatusError(message);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [requestToken]);

  useEffect(() => {
    if (loading || checking) return;
    if (!user) {
      navigate('/portal/login', { replace: true, state: { from: '/portal/complete-purchase' } });
      return;
    }
    void loadStatus();
  }, [checking, loadStatus, loading, navigate, user]);

  const handleStartCheckout = async () => {
    if (!requestToken) return;
    setStartingCheckout(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-domain-purchase', {
        body: {
          action: 'start-checkout',
          requestToken,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to start checkout.');
      }
      if (data?.url) {
        window.location.assign(data.url as string);
        return;
      }
      if (data?.status === 'completed') {
        toast.success('Purchase already completed.');
        await loadStatus();
        return;
      }
      throw new Error('No checkout URL returned.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to start checkout.';
      toast.error(message);
      await loadStatus();
    } finally {
      setStartingCheckout(false);
    }
  };

  if (loading || checking || statusLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Complete Purchase | ShadowScan Portal</title>
          <meta
            name="description"
            content="Complete customer-approved billing activation for configured ShadowScan domains."
          />
        </Helmet>
        <div className="flex items-center gap-2 text-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading purchase request...</span>
        </div>
      </div>
    );
  }

  const currentStatus = status?.request?.status;
  const actionable = currentStatus === 'pending' || currentStatus === 'checkout_started';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Complete Purchase | ShadowScan Portal</title>
        <meta
          name="description"
          content="Review pending domains and complete Stripe checkout to activate ShadowScan billing and monitoring."
        />
      </Helmet>
      <Card className="w-full max-w-2xl cyber-card">
        <CardHeader>
          <CardTitle>{formatStatusTitle(currentStatus || 'revoked')}</CardTitle>
          <CardDescription>
            {statusError
              ? statusError
              : formatStatusDescription(currentStatus || 'revoked')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!!status?.domains?.length && (
            <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-2">
              <p className="text-sm font-medium text-foreground">Configured domains</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {status.domains.map((domain) => (
                  <li key={domain.id} className="flex items-center justify-between gap-2">
                    <span className="font-mono text-foreground">{domain.domainName}</span>
                    <span className="capitalize">{domain.tier}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {status?.request?.expiresAt && (
            <p className="text-xs text-muted-foreground">
              Link expiry: {new Date(status.request.expiresAt).toLocaleString()}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            {actionable && !statusError ? (
              <Button className="flex-1" onClick={handleStartCheckout} disabled={startingCheckout}>
                {startingCheckout ? 'Opening checkout...' : 'Complete Purchase'}
              </Button>
            ) : (
              <Button className="flex-1" onClick={() => navigate('/portal')}>
                Return to Portal
              </Button>
            )}
            <Button variant="outline" onClick={() => void loadStatus()} disabled={statusLoading || startingCheckout}>
              Refresh Status
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
